<template>
  <div :class="$style.wrapper">
    <ProductMessage />
    <div class="c-form__row">
      <div data-cy="onboarding-revenue-accounts">
        <ConnectAccountsLayout
          :show-error="showError"
          :show-product-message="false"
          :event-tracking-fields="eventTrackingFields"
          :custom-accounting-error="errorMessage"
        >
          <ConnectedAccountSalesTileGroup
            :show-error="showError"
            v-model:error-message="errorMessage"
            :event-tracking-fields="eventTrackingFields"
            :load-all-platforms="true"
          />
        </ConnectAccountsLayout>
      </div>

      <div class="mt-6 p-4 bg-gray-50 rounded-lg">
        <p class="text-sm text-gray-600">
          <strong>Note:</strong> You can skip this step and connect your revenue
          accounts later from your dashboard.
        </p>
      </div>
    </div>

    <div class="c-form__controls">
      <DSButton
        v-if="previousRoute"
        label="Back"
        class="mr-auto p-button-link"
        :class="[$style['button-back']]"
        @click="goToPreviousStep"
      />
      <DSButton
        :label="hasConnectedRevenueAccounts ? 'Continue' : 'Skip for Now'"
        :class="$style.button"
        :loading="isLoading"
        :disabled="isLoading"
        @click="goToNextStep"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from '@/utils/router-helper';
import { RouteNames } from '@/data/routes';
import DSButton from 'primevue/button';
import ProductMessage from '@/components/banners/ProductMessage.vue';
import ConnectAccountsLayout from '@/components/layouts/ConnectAccountsLayout.vue';
import ConnectedAccountSalesTileGroup from '@/pages/connected-accounts/components/ConnectedAccountSalesTileGroup.vue';
import { OnboardingStep } from '@/types/onboarding.types';
import { getAdjacentInvitedUserOnboardingRoutes } from '@/composables/useInvitedUserRouting';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

const store = useStore();
const router = useRouter();

const showError = ref(false);
const errorMessage = ref('');
const isLoading = ref(false);
const eventTrackingFields = ref({
  page: 'onboarding_revenue_account',
  section: 'revenue_account_connection',
});

const {
  previousRouteName: previousInvitedRouteName,
  nextRouteName: nextInvitedRouteName,
} = getAdjacentInvitedUserOnboardingRoutes();

const currentStep = computed<{
  previousRoute?: OnboardingStep;
  nextRoute?: OnboardingStep;
}>(
  () =>
    store.getters.paymentsOnboardingSteps[
      RouteNames.ONBOARDING.REVENUE_ACCOUNT
    ],
);

const props = defineProps<{
  invitedRoute?: boolean;
}>();

const previousRoute = computed(() =>
  props.invitedRoute
    ? previousInvitedRouteName
    : currentStep.value?.previousRoute?.name,
);

const nextRoute = computed(() =>
  props.invitedRoute
    ? nextInvitedRouteName
    : currentStep.value?.nextRoute?.name,
);

const hasConnectedRevenueAccounts = computed<boolean>(() => {
  return store.getters.connectedSalesAccounts.length > 0;
});

onMounted(async () => {
  await store.dispatchApiAction('FETCH_USER_EXTERNAL_ACCOUNTS');
});

const goToPreviousStep = () => {
  if (previousRoute.value) {
    router.push({ name: previousRoute.value });
  }
};

const goToNextStep = async () => {
  if (isLoading.value) return;

  isLoading.value = true;

  try {
    // Track the skip event only if there are no connected revenue accounts
    if (!hasConnectedRevenueAccounts.value) {
      analytics.track(
        SEGMENT_EVENTS.if_revenue_account_initial_onboarding_skipped,
      );
    }

    await Promise.all([
      store.dispatchApiAction('FETCH_USER_BANK_ACCOUNTS'),
      store.dispatchApiAction('GET_DEBT_OBLIGATIONS'),
      store.dispatchApiAction('GET_BUSINESS_CAPACITY'),
    ]);

    await store.dispatch('UPDATE_ALL_PAYMENTS_BIZ_PROFILE_STEPS');

    store.dispatch(
      'UPDATE_PAYMENTS_ONBOARDING_STEP',
      RouteNames.ONBOARDING.REVENUE_ACCOUNT,
    );

    if (nextRoute.value) {
      router.push({ name: nextRoute.value });
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="less" module>
.wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.button {
  min-width: 186px;
}

button.button-back {
  padding-left: 0;
}
</style>
